import { useState, useEffect, useRef } from 'react';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import {
  Activity, Archive, BarChart2, BarChart3, Bell, BookOpen, Calendar,
  CheckSquare, Eye, FlaskConical, HelpCircle, History, Layers, LineChart,
  LogOut, MoreHorizontal, Package, Settings, SlidersHorizontal, Sprout,
  Thermometer, X, Zap, type LucideIcon,
} from 'lucide-react';

const ICON_MAP: Record<string, LucideIcon> = {
  Activity, Archive, BarChart2, BarChart3, Bell, BookOpen, Calendar,
  CheckSquare, Eye, FlaskConical, History, Layers, LineChart,
  Package, Settings, SlidersHorizontal, Sprout, Thermometer, Zap,
};
import { useLocation, useNavigate } from 'react-router-dom';
import { useSensoryFeedback } from '../hooks/useSensoryFeedback';
import { navigationSchema } from '../config/navigationSchema';

interface Props {
  unreadAlerts: number;
  onAlertsClick: () => void;
  onSettingsClick: () => void;
  onLogout?: () => void;
  userEmail?: string;
}

// Ana bar: cockpit → grow → climate → AI Asistan
const cockpitItem = navigationSchema.find(item => item.category === 'cockpit')!;
const growItem = navigationSchema.find(item => item.category === 'grow')!;
const climateItem = navigationSchema.find(item => item.category === 'climate')!;
const aiItem = navigationSchema.find(item => item.id === 'ai_jarvis')!;

const MAIN_NAV = [
  { id: cockpitItem.id, label: 'Komuta',     iconName: cockpitItem.icon, path: cockpitItem.path, tabId: cockpitItem.tabId },
  { id: climateItem.id, label: 'İklim',       iconName: climateItem.icon, path: climateItem.path, tabId: climateItem.tabId },
  { id: growItem.id,    label: 'Üretim',      iconName: growItem.icon,    path: growItem.path,    tabId: growItem.tabId },
  { id: aiItem.id,      label: 'AI Asistan',  iconName: aiItem.icon,      path: aiItem.path,      tabId: aiItem.tabId },
];

// "Daha" sheet: Uyarılar + Operasyon + Sistem + Ayarlar
const MORE_PATHS = ['/uyarilar', '/operasyon', '/sistem', '/ayarlar'];

export default function MobileBottomNav({ unreadAlerts, onLogout, userEmail }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [moreOpen, setMoreOpen] = useState(false);
  const { triggerInteraction } = useSensoryFeedback();
  const sheetRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useBodyScrollLock(moreOpen);

  useEffect(() => {
    if (moreOpen) {
      closeButtonRef.current?.focus();
    }
  }, [moreOpen]);

  useEffect(() => {
    if (!moreOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMoreOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const focusable = Array.from(
        sheet.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [moreOpen]);

  const isActive = (path: string) =>
    path === '/' ? currentPath === '/' : currentPath.startsWith(path);

  const handleMoreItem = (action: () => void) => {
    triggerInteraction();
    setMoreOpen(false);
    action();
  };

  const moreItems = MORE_PATHS.map(path => {
    const item = navigationSchema.find(i => i.path === path)!;
    return {
      label: item.label,
      iconName: item.icon,
      path: item.path,
      tabId: item.tabId,
      isAlerts: path === '/uyarilar',
    };
  });

  const moreBadgeVisible = unreadAlerts > 0 && currentPath !== '/uyarilar';
  const moreActive = moreOpen || MORE_PATHS.some(p => currentPath.startsWith(p));

  return (
    <>
      {/* More sheet overlay */}
      {moreOpen && (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-[110] md:hidden"
          style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setMoreOpen(false)}
        />
      )}

      {/* More sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label="Daha Fazlası"
        aria-hidden={!moreOpen}
        className={`fixed left-0 right-0 z-[120] md:hidden transition-transform duration-300 ease-out ${moreOpen ? 'translate-y-0' : 'translate-y-full'}`}
        style={{
          bottom: 0,
          paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
          background: 'rgba(18,20,25,0.98)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid var(--line)',
          borderRadius: '20px 20px 0 0',
        }}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-4)' }}>Daha Fazlası</span>
          <button
            ref={closeButtonRef}
            onClick={() => { triggerInteraction(); setMoreOpen(false); }}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: 'var(--line)', color: 'var(--text-3)' }}
            aria-label="Kapat"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="px-3 pb-4 space-y-1">
          {moreItems.map(({ label, iconName, path, tabId, isAlerts }) => {
            const Icon = ICON_MAP[iconName] ?? HelpCircle;
            const active = isActive(path);
            return (
              <button
                key={path}
                onClick={() => handleMoreItem(() => {
                  if (tabId) {
                    navigate(`${path}?tab=${tabId}`);
                  } else {
                    navigate(path);
                  }
                })}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all cursor-pointer text-left"
                style={{ color: active ? 'var(--ok)' : isAlerts && unreadAlerts > 0 ? 'var(--crit)' : 'var(--text-2)' }}
                onTouchStart={e => (e.currentTarget.style.background = 'var(--line)')}
                onTouchEnd={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div className="relative">
                  <Icon className="w-5 h-5 flex-shrink-0" style={{ color: active ? 'var(--ok)' : isAlerts && unreadAlerts > 0 ? 'var(--crit)' : 'var(--text-3)' }} />
                  {isAlerts && unreadAlerts > 0 && !active && (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 flex items-center justify-center rounded-full px-0.5 text-[8px] font-bold text-white" style={{ background: 'var(--crit)' }}>
                      {unreadAlerts > 9 ? '9+' : unreadAlerts}
                    </span>
                  )}
                </div>
                <span className="text-sm font-semibold">{label}</span>
              </button>
            );
          })}

          {onLogout && (
            <>
              <div className="mx-4 my-2" style={{ height: 1, background: 'var(--line)' }} />
              <button
                onClick={() => handleMoreItem(onLogout)}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl transition-all cursor-pointer text-left"
                style={{ color: 'var(--crit)' }}
                onTouchStart={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.08)')}
                onTouchEnd={e => (e.currentTarget.style.background = 'transparent')}
              >
                <LogOut className="w-5 h-5 flex-shrink-0" />
                <div className="flex flex-col">
                  <span className="text-sm font-semibold">Çıkış Yap</span>
                  {userEmail && <span className="text-[10px] opacity-60">{userEmail}</span>}
                </div>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bottom nav bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-[100] md:hidden safe-area-bottom"
        style={{ background: 'rgba(15,17,21,0.97)', backdropFilter: 'blur(16px)', borderTop: '1px solid var(--line)' }}
      >
        <div className="flex items-center h-14 px-2">
          {MAIN_NAV.map(({ id, label, iconName, path, tabId }) => {
            const active = isActive(path);
            const Icon = ICON_MAP[iconName] ?? HelpCircle;
            return (
              <button
                key={id}
                onClick={() => {
                  triggerInteraction();
                  if (tabId) {
                    navigate(`${path}?tab=${tabId}`);
                  } else {
                    navigate(path);
                  }
                }}
                onPointerDown={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.93)'; }}
                onPointerUp={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
                onPointerLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
                className="relative flex flex-col items-center justify-center gap-0.5 flex-1 h-12 rounded-xl transition-all cursor-pointer"
                style={{
                  color: active ? 'var(--ok)' : 'var(--text-4)',
                  background: active ? 'rgba(110,231,183,0.10)' : 'transparent',
                  transition: 'background 0.15s ease, color 0.15s ease, transform 0.1s ease',
                }}
              >
                <Icon className="w-5 h-5" style={{ transform: active ? 'translateY(-1px) scale(1.08)' : 'none', transition: 'transform 0.15s ease' }} />
                <span style={{ fontSize: 'var(--text-nav-mobile)', fontWeight: 600, letterSpacing: '0.02em' }}>{label}</span>
              </button>
            );
          })}

          {/* Daha — with unread alert badge */}
          <button
            onClick={() => { triggerInteraction(); setMoreOpen(v => !v); }}
            onPointerDown={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.93)'; }}
            onPointerUp={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
            onPointerLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
            className="relative flex flex-col items-center justify-center gap-0.5 flex-1 h-12 rounded-xl cursor-pointer"
            style={{
              color: moreActive ? 'var(--ok)' : moreBadgeVisible ? 'var(--crit)' : 'var(--text-4)',
              background: moreActive ? 'rgba(110,231,183,0.10)' : 'transparent',
              transition: 'background 0.15s ease, color 0.15s ease, transform 0.1s ease',
            }}
          >
            <div className="relative" style={{ transform: moreActive ? 'translateY(-1px) scale(1.08)' : 'none', transition: 'transform 0.15s ease' }}>
              <MoreHorizontal className="w-5 h-5" />
              {moreBadgeVisible && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center rounded-full px-1 text-[9px] font-bold text-white" style={{ background: 'var(--crit)' }}>
                  {unreadAlerts > 9 ? '9+' : unreadAlerts}
                </span>
              )}
            </div>
            <span style={{ fontSize: 'var(--text-nav-mobile)', fontWeight: 600, letterSpacing: '0.02em' }}>Daha</span>
          </button>
        </div>
      </nav>
    </>
  );
}
